import {
  type FundCorrection,
  type FundSelectionVersion,
  isSubPortfolioLifeRef,
  type ProfileVersionV2,
} from '@festina/unitlink-general-types/unitlink'
import { type Optional, typeUtils, type ValidatedKey } from '@festina-life/core'
import { FormikDoubleInput, FormikSelect } from '@festina-life/ui/formikForms'
import { useTranslate, useTranslateEnum } from '@festina-life/ui/hooks'
import { useField } from 'formik'

import { useHook } from '../../../lenteHooks'

type Props = {
  fieldNamePrefix: ValidatedKey
  coverKey: Optional<string>
}

export const FundCorrectionFields = ({ fieldNamePrefix, coverKey }: Props) => {
  const translate = useTranslate()
  const translateEnum = useTranslateEnum()
  const { ownerId, tenantId } = useHook('routing.general.useEntityParams')()

  const { data: portfolioVersion } = useHook(
    'fetchers.unitlink.useFetchPortfolioVersionLatest',
  )({
    ownerId,
    tenantId,
  })

  const validateKey =
    typeUtils.createNestedValidator<FundCorrection>(fieldNamePrefix)

  const partitionKeysToProfileVersion =
    portfolioVersion?.subPortfolioGroupVersions
      ?.flatMap(
        (subPortfolioGroupVersion) =>
          subPortfolioGroupVersion?.subPortfolioVersions,
      )
      .filter(
        (subPortfolioVersion) =>
          isSubPortfolioLifeRef(subPortfolioVersion?.subPortfolioRef) &&
          subPortfolioVersion.subPortfolioRef.coverKey === coverKey,
      )
      .flatMap((subPortfolioVersion) => subPortfolioVersion?.profiles)
      .reduce<Record<string, ProfileVersionV2>>((acc, profileVersion) => {
        if (profileVersion?.subPortfolioPartitionKey) {
          acc[profileVersion.subPortfolioPartitionKey] = profileVersion
        }

        return acc
      }, {}) ?? {}

  const [{ value: partitionKey }] = useField<string>(
    validateKey('partitionKey'),
  )

  const isinKeyToFundSelectionVersion =
    partitionKeysToProfileVersion[
      partitionKey
    ]?.subPartitionFundVersions?.reduce<Record<string, FundSelectionVersion>>(
      (acc, fundSelectionVersion) => {
        if (fundSelectionVersion?.isinKey) {
          acc[fundSelectionVersion.isinKey] = fundSelectionVersion
        }

        return acc
      },
      {},
    ) ?? {}

  return (
    <>
      <FormikSelect
        name={validateKey('partitionKey')}
        label={translate('fundCorrection.partitionKey')}
        options={Object.keys(partitionKeysToProfileVersion)}
        renderFn={(partitionKey) => {
          const profileVersion = partitionKeysToProfileVersion[partitionKey]

          return profileVersion
            ? translateEnum(
                'unitLink.enum.ProfileType',
                profileVersion.profileType,
              )
            : partitionKey
        }}
      />
      <FormikSelect
        name={validateKey('isinKey')}
        label={translate('fundCorrection.isinKey')}
        options={Object.keys(isinKeyToFundSelectionVersion)}
        renderFn={(isinKey) => {
          const fundSelectionVersion = isinKeyToFundSelectionVersion[isinKey]
          return fundSelectionVersion
            ? `${translateEnum('unitLink.enum.ProfileType', fundSelectionVersion.fundName)} (${fundSelectionVersion.isinKey})`
            : isinKey
        }}
      />
      <FormikDoubleInput
        name={validateKey('amount')}
        label={translate('fundCorrection.amount')}
      />
      <FormikDoubleInput
        name={validateKey('units')}
        label={translate('fundCorrection.units')}
      />
    </>
  )
}
